import { CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useReducer, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useDebounce } from "usehooks-ts";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import Pagination from "../../../components/ui/Pagination";
import RenderTable from "../../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../../config/constants";
import { colors } from "../../../config/theme";
import { useAuthContext } from "../../../context/auth";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../../context/bulkDeleteReducer";
import { Chip, toQuery } from "../../../helpers/general";
import { useCookie } from "../../../hooks/useCookie";
import { useQueryParam } from "../../../hooks/useQueryParam";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";
import PlacesTable from "../PlaceTable";
import { cardStyle } from "../PlacesTable";
import { controlStyle } from "../../../helpers/constants";

const PAGE_SIZE = 10;

export default function AdminPlacesView() {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const [searchParams] = useSearchParams();
  const { addMultiple, add } = useQueryParam();

  // The URL is the source of truth for search and page
  const urlSearch = searchParams.get("search") ?? "";
  const currentPage = Number(searchParams.get("page")) || 1;

  // Local input state, so typing is instant and the URL updates once debounced
  const [input, setInput] = useState(urlSearch);
  const debouncedInput = useDebounce(input, 400);

  useEffect(() => {
    if (debouncedInput !== urlSearch) {
      addMultiple({ search: debouncedInput, page: "1" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput]);

  const { data, isLoading, isPreviousData } = useQuery(
    ["getAllPlaces", currentPage, urlSearch],
    () =>
      apiService.MakeGetRequest(
        `places${toQuery({ page: currentPage, limit: PAGE_SIZE, q: urlSearch })}`,
        token,
      ),
    { keepPreviousData: true, enabled: Boolean(token) },
  );

  const deletePlace = useMutation((id: string) =>
    apiService.MakeDeleteRequest(`places/${id}`, token),
  );

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      deletePlace.mutate(id, {
        onError: () => toastError(general.fr.message.operationFailed),
        onSuccess() {
          toastSuccess(general.fr.message.placeDeleted);
          dispatch({ type: DELETE_ACTIONS.REMOVE, id });
          queryClient.invalidateQueries("getAllPlaces");
        },
      });
    });
  };

  const total = data?.meta?.totalItems ?? 0;

  return (
    <section>
      {/* Title + count + add */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fs-4 fw-bold mb-0">Établissements</h5>
          {data && (
            <Chip tone="warning">
              {total} {total > 1 ? "Établissements" : "Établissement"}
            </Chip>
          )}
        </div>

        <Link
          to="/places/new"
          className="btn btn-primary text-white rounded-pill px-4"
        >
          + Ajouter un établissement
        </Link>
      </div>

      {/* Toolbar */}
      <div
        style={{ ...cardStyle, padding: "12px 16px" }}
        className="d-flex flex-wrap align-items-center gap-2 mb-3"
      >
        <div
          className="d-flex align-items-center gap-2 px-3 flex-grow-1"
          style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
        >
          <MagnifyingGlassIcon color="gray" width={18} />
          <input
            type="text"
            placeholder="Rechercher un établissement"
            className="border-0 w-100 bg-transparent"
            style={{ outline: "none", fontSize: 13 }}
            value={input}
            onChange={(e) => setInput(e.target.value.toLowerCase())}
          />
        </div>

        <button
          type="button"
          disabled={deletePlace.isLoading || selectedCount === 0}
          onClick={handleBulkDelete}
          className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
        >
          {deletePlace.isLoading ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            <>
              <DeleteIcon fill={colors.primary} /> Supprimer
              {selectedCount > 0 && ` (${selectedCount})`}
            </>
          )}
        </button>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isLoading || isPreviousData}
          render={() =>
            data?.items?.length > 0 || user?.adminForPlace ? (
              <PlacesTable
                places={data?.items as Place[]}
                placesToBeDeletedList={dispatch}
              />
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={(page) => add("page", String(page))}
        siblingCount={1}
        totalCount={data?.meta?.totalPages}
        pageSize={PAGE_SIZE}
      />
    </section>
  );
}
